// 購入の結果を、アプリ全体で 1 か所で受け取る（DialogPage に置く）。
// 以前は画面ごとに受け取っていて、トーストが 2 回出る・同期が 2 回走るなどしていた
// - ネイティブの購入の結果（Command subscription）
// - Stripe から戻ったとき（success_url に session_id が付く）
import { useEffect } from "react";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import { useDialog } from "app/hook";
import { DialogType } from "app/types";
import { RootState, AppDispatch } from "app/store";
import I18n from "common/i18n";
import { sleep } from "common/utils";
import parentI18n from "features/parent/i18n";
import { fetchAccountPlan } from "./api";
import { PurchaseAudience, takePurchaseAudience } from "./purchaseContext";
import { NativePurchaseResult, recordNativePurchaseResult } from "./purchaseAnalytics";

const showToast = (message: string) => {
  toast(
    <div>
      <p className="text text-textcolor-black">{message}</p>
    </div>,
  );
};

const successMessage = (audience: PurchaseAudience) =>
  audience === PurchaseAudience.PARENT
    ? parentI18n.t("MSG_SUBSCRIPTION_PURCHASE_SUCCESS")
    : I18n.t("MSG_USER_PLAN_PURCHASE_SUCCESS");

const errorMessage = (audience: PurchaseAudience) =>
  audience === PurchaseAudience.PARENT
    ? parentI18n.t("MSG_SUBSCRIPTION_PURCHASE_ERROR")
    : I18n.t("MSG_USER_PLAN_PURCHASE_ERROR");

export const PurchaseResultHandler = () => {
  const dispatch = useDispatch<AppDispatch>();
  const handleDialog = useDialog();
  const parent = useSelector((state: RootState) => state.user.parent);

  // ネイティブの購入の結果
  useEffect(() => {
    const handleMessage = async (message: MessageEvent) => {
      let parsed: { command?: string; data?: { result?: string; error_code?: string } };
      try {
        parsed = JSON.parse(message.data);
      } catch {
        // JSON でないメッセージは無視する
        return;
      }
      if (parsed?.command !== "subscription") return;

      const audience = takePurchaseAudience();
      const result = recordNativePurchaseResult(parsed.data);
      if (result === NativePurchaseResult.SUCCESS) {
        await sleep(5000);
        try {
          if (parent?.uid) {
            await dispatch(fetchAccountPlan({ uid: parent.uid, forceSync: true })).unwrap();
          }
        } catch (err) {
          console.error("Purchase sync error:", err);
        }
        handleDialog({ type: DialogType.LOADING, value: false });
        showToast(successMessage(audience));
      } else if (result === NativePurchaseResult.CANCELLED) {
        // 利用者がやめただけなので、エラーは出さない
        handleDialog({ type: DialogType.LOADING, value: false });
      } else {
        handleDialog({ type: DialogType.LOADING, value: false });
        showToast(errorMessage(audience));
      }
    };

    if (!window.ReactNativeWebView) return;
    if (window.kidsAppPlatform === "ios") {
      window.addEventListener("message", handleMessage);
    } else {
      document.addEventListener("message", handleMessage as EventListener);
    }
    return () => {
      if (window.kidsAppPlatform === "ios") {
        window.removeEventListener("message", handleMessage);
      } else {
        document.removeEventListener("message", handleMessage as EventListener);
      }
    };
  }, [dispatch, handleDialog, parent?.uid]);

  // Stripe から戻ったとき
  useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("session_id") || !parent?.uid) return;

    const audience = takePurchaseAudience();
    handleDialog({ type: DialogType.LOADING, value: true });
    const syncAfterStripeRedirect = async () => {
      try {
        await sleep(3000); // Webhook の処理を待つ
        await dispatch(fetchAccountPlan({ uid: parent.uid, forceSync: true })).unwrap();
        showToast(successMessage(audience));
      } catch (err) {
        console.error("Stripe redirect sync error:", err);
      } finally {
        handleDialog({ type: DialogType.LOADING, value: false });
        // 戻り先（success_url）から session_id だけを外す
        url.searchParams.delete("session_id");
        window.history.replaceState({}, "", `${url.pathname}${url.search}`);
      }
    };
    syncAfterStripeRedirect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parent?.uid]);

  return null;
};
